import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEMO_TODAY, money, units as formatUnits } from '../../lib/format';
import { CHANGE_LABELS } from '../../lib/labels';
import { useRegistry } from '../../store/RegistryStore';
import { useInvestorScope } from '../../store/selectors';
import { useToast } from '../../ui/feedback';
import { AboutThisPage, StatusChip } from '../../ui/primitives';
import { Card, InvestorTitle, SectionLabel, shortEntityName } from '../common';

export default function RequestsPage() {
  const scope = useInvestorScope();
  const { dispatch } = useRegistry();
  const toast = useToast();
  const [params] = useSearchParams();
  const initialKey =
    params.get('holder') && params.get('class')
      ? `${params.get('holder')}|${params.get('class')}`
      : '';
  const [holdingKey, setHoldingKey] = useState(
    initialKey ||
      (scope.holdings[0] ? `${scope.holdings[0].holderId}|${scope.holdings[0].classId}` : ''),
  );
  const [kind, setKind] = useState<'REDEMPTION' | 'TRANSFER'>('REDEMPTION');
  const [unitsText, setUnitsText] = useState('');
  const [transferee, setTransferee] = useState('');
  const holding = scope.holdings.find((item) => `${item.holderId}|${item.classId}` === holdingKey);
  const fund = scope.funds.find((item) => item.id === holding?.fundId);
  const unitClass = scope.classes.find((item) => item.id === holding?.classId);
  const redeemable = fund?.basis === 'NAV_UNITS';
  const effectiveKind = redeemable ? kind : 'TRANSFER';
  const requested = Number(unitsText) || 0;
  const valid =
    holding &&
    requested > 0 &&
    requested <= holding.units &&
    (effectiveKind === 'REDEMPTION' || transferee.trim().length > 2);

  return (
    <>
      <InvestorTitle
        title="Transfers & redemptions"
        subtitle="Request to redeem units or transfer them to another person or entity."
      />
      <AboutThisPage>
        <p>
          Open-ended funds accept redemptions at the next quarterly window, priced at NAV. Closed
          funds don&apos;t offer redemptions, but units can be transferred off-market to another
          wholesale investor with the trustee&apos;s approval.
        </p>
      </AboutThisPage>
      <Card>
        <Stack spacing={2}>
          <TextField
            select
            label="Holding"
            value={holdingKey}
            onChange={(event) => setHoldingKey(event.target.value)}
          >
            {scope.holdings.map((item) => {
              const holder = scope.holders.find((candidate) => candidate.id === item.holderId);
              return (
                <MenuItem
                  key={`${item.holderId}|${item.classId}`}
                  value={`${item.holderId}|${item.classId}`}
                >
                  {scope.funds.find((candidate) => candidate.id === item.fundId)?.code} ·{' '}
                  {holder ? shortEntityName(holder) : ''} · {formatUnits(item.units)} units
                </MenuItem>
              );
            })}
          </TextField>
          {redeemable ? (
            <ToggleButtonGroup
              exclusive
              value={kind}
              onChange={(_event, next) => next && setKind(next)}
              size="small"
            >
              <ToggleButton value="REDEMPTION">Redeem</ToggleButton>
              <ToggleButton value="TRANSFER">Transfer</ToggleButton>
            </ToggleButtonGroup>
          ) : (
            <Alert severity="info">
              {fund?.name} is closed-ended, so only transfers are available.
            </Alert>
          )}
          <TextField
            label="Units"
            value={unitsText}
            onChange={(event) => setUnitsText(event.target.value)}
            helperText={
              holding
                ? `You hold ${formatUnits(holding.units)} units (≈ ${money(requested * (unitClass?.unitPrice ?? 1))} at today's price)`
                : undefined
            }
          />
          {effectiveKind === 'TRANSFER' && (
            <TextField
              label="Transfer to (name of person or entity)"
              value={transferee}
              onChange={(event) => setTransferee(event.target.value)}
              helperText="They'll be asked to complete identity and wholesale checks."
            />
          )}
          <Box>
            <Button
              variant="contained"
              disabled={!valid}
              onClick={() => {
                if (!holding) return;
                dispatch({
                  type: 'CHANGE_REQUEST_CREATE',
                  request: {
                    holderId: holding.holderId,
                    kind: effectiveKind,
                    channel: 'INVESTOR_PORTAL',
                    submittedAt: DEMO_TODAY,
                    fundId: holding.fundId,
                    classId: holding.classId,
                    units: requested,
                    details:
                      effectiveKind === 'REDEMPTION'
                        ? [
                            { label: 'Redemption window', value: 'Quarterly, 31 Dec 2026' },
                            { label: 'Price', value: 'NAV at window date' },
                          ]
                        : [
                            { label: 'Transferee', value: transferee.trim() },
                            { label: 'Consideration', value: 'To be confirmed' },
                          ],
                    verification: ['MFA step-up passed'],
                  },
                });
                toast('Request submitted to the registry');
                setUnitsText('');
                setTransferee('');
              }}
            >
              Submit request
            </Button>
          </Box>
        </Stack>
      </Card>
      <SectionLabel>Your requests</SectionLabel>
      <Card sx={{ p: 0 }}>
        {scope.changeRequests.filter(
          (item) => item.kind === 'REDEMPTION' || item.kind === 'TRANSFER',
        ).length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No transfer or redemption requests yet.
          </Typography>
        )}
        {scope.changeRequests
          .filter((item) => item.kind === 'REDEMPTION' || item.kind === 'TRANSFER')
          .map((request, index) => (
            <Stack
              key={request.id}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                px: 2,
                py: 1.5,
                borderTop: index ? '1px solid' : 'none',
                borderColor: 'divider',
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {CHANGE_LABELS[request.kind]} · {formatUnits(request.units ?? 0)} units
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {request.reference}
                </Typography>
              </Box>
              <StatusChip status={request.status} />
            </Stack>
          ))}
      </Card>
    </>
  );
}
